import { ElementRef, Injectable, inject } from '@angular/core';
import { EventManager } from '@angular/platform-browser';

import { Observable, ReplaySubject, Subject, filter, interval, map, race, skipWhile, take, tap, timer } from 'rxjs';

import { isObject } from '../helpers';
import { PlatformService } from '../platform/platform.service';
import { WindowRefService } from '../window-ref/window-ref.service';

import { ScrollIntoViewAlignmentType, ScrollIntoViewBlock } from './scroll.types';

@Injectable({
  providedIn: 'root'
})
export class ScrollService {
  static readonly scrollDelay = 100;

  private readonly eventManager = inject(EventManager);
  private readonly win = inject<WindowRefService>(WindowRefService).nativeWindow;
  private readonly docElement = this.win.document.documentElement;
  private readonly isBrowser = inject(PlatformService).isBrowser;
  private readonly scrollSubject = new Subject<number>();

  private pendingTargets: (ElementRef | HTMLElement)[] = [];
  // eslint-disable-next-line @typescript-eslint/ban-types
  private removeScrollHandler: Function;

  get scroll$(): Observable<number> {
    if (!this.removeScrollHandler) {
      this.addScrollListener();
    }
    return this.scrollSubject.asObservable();
  }

  scrollTo(
    el: ElementRef | HTMLElement,
    alignment: ScrollIntoViewAlignmentType = ScrollIntoViewBlock.CENTER,
    delay = ScrollService.scrollDelay
  ): Observable<void> {
    const target = el instanceof ElementRef ? el.nativeElement : el;
    const scrollToSubject = new ReplaySubject<void>();

    if (this.isBrowser && target && !this.pendingTargets.includes(target)) {
      this.pendingTargets.push(target);
      setTimeout(() => {
        this.doScroll(target, alignment, scrollToSubject);
      }, delay);
    } else {
      this.resolve(scrollToSubject);
    }

    return scrollToSubject.asObservable();
  }

  triggerScroll(): void {
    if (!this.scrollSubject.observed) {
      this.removeScrollListener();
    } else {
      this.scrollSubject.next(this.docElement.scrollTop);
    }
  }

  /**
   * Prevent next immediate scroll position change by subscribing
   * to scroll event and resetting scroll position to current value.
   * If no scroll event happens within next 0.25sec,
   * the scroll subscription will be terminated.
   */
  preventNextScroll(): void {
    const { scrollTop } = this.docElement;
    race(this.scroll$, timer(250).pipe(map(() => null)))
      .pipe(
        take(1),
        filter(pos => pos !== null)
      )
      .subscribe(() => this.docElement.scrollTo(0, scrollTop));
  }

  private addScrollListener(): void {
    this.removeScrollHandler = this.eventManager.addEventListener(
      this.win as unknown as HTMLElement,
      'scroll',
      this.triggerScroll.bind(this)
    );
  }

  private removeScrollListener(): void {
    if (this.removeScrollHandler) {
      this.removeScrollHandler();
      this.removeScrollHandler = null;
    }
  }

  private doScroll(
    target: HTMLElement,
    alignment: ScrollIntoViewAlignmentType,
    scrollToSubject: ReplaySubject<void>
  ): void {
    const parentElement = target.parentElement;
    const { block, inline = null } = isObject(alignment) ? alignment : { block: alignment };

    const resolveAfter = (): void => {
      this.pendingTargets = this.pendingTargets.filter(tgt => tgt !== target);
      this.resolve(scrollToSubject);
    };

    target.scrollIntoView({
      behavior: 'smooth',
      block,
      ...(inline ? { inline } : {})
    } as ScrollIntoViewOptions);

    if (block === ScrollIntoViewBlock.START) {
      parentElement.scrollTop > 1
        ? interval(ScrollService.scrollDelay)
            .pipe(
              // wait for scrollTop to be reached or resolve after 15 iterations if not
              skipWhile(count => parentElement.scrollTop > 1 && count < 15),
              tap(() => (parentElement.scrollTop = 0)),
              take(1)
            )
            .subscribe(resolveAfter)
        : resolveAfter();
      return;
    }

    setTimeout(resolveAfter, ScrollService.scrollDelay);
  }

  private resolve(scrollToSubject: ReplaySubject<void>): void {
    scrollToSubject.next();
    scrollToSubject.complete();
  }
}
